// import * as d3 from 'd3'
import * as d3 from 'd3';
import _ from 'lodash';
import {
  CHART_TYPE_POLYLINE,
  CHART_TYPE_POINT,
  CHART_TYPE_HISTOGRAM,
  CHART_TYPE_AREA,
  CHART_TYPE_FILL_AREA,
  CHART_TYPE_MULTI_COLOR_FILL_AREA
} from '@client/pages/activity-detail/config/chart.types.js';
import { Chart } from '../chart.js';

export class TimeChart extends Chart {
  // 画暂停区间线
  drawPauseLine(pauseList = []) {
    const that = this;
    let pause = that.svg.select('.pause');

    // 定义直线画法
    const lineFun = d3
      .line()
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    pause.remove();
    pause = that.svg.append('g').attr('class', 'pause').attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);

    for (let n = 0, length = pauseList.length; n < length; n++) {
      const item = pauseList[n];
      pause
        .append('path')
        .attr('class', 'pause-line')
        .attr(
          'd',
          lineFun([
            {
              x: that.xScale(item[that.xAxisKey]),
              y: 0
            },
            {
              x: that.xScale(item[that.xAxisKey]),
              y: that.contentHeight
            }
          ])
        )
        .attr('fill', 'none')
        .attr('stroke-width', 1)
        .attr('stroke', that.getFillColor('#808080'))
        .attr('stroke-dasharray', '10,10');
    }
  }

  // 画图表
  drawChart(attr) {
    const that = this;
    switch (attr.chartType) {
      case CHART_TYPE_POLYLINE:
        that.drawPolylineChart(attr);
        break;
      case CHART_TYPE_POINT:
        that.drawPointChart(attr);
        break;
      case CHART_TYPE_HISTOGRAM:
        that.drawHistogramChart(attr);
        break;
      case CHART_TYPE_FILL_AREA:
        that.drawFillAreaChart(attr);
        break;
      case CHART_TYPE_MULTI_COLOR_FILL_AREA:
        this.drawMultiColorFillAreaChart(attr);
        break;
      case CHART_TYPE_AREA:
        that.drawAreaChart(attr);
        break;
      case 'drawPowerAndAirPowerChart':
        that.drawPowerAndAirPowerChart(attr);
        break;
      case 'drawAirPowerChart':
        that.drawAirPowerChart(attr);
        break;
    }
  }

  // 画功率风阻河流图
  drawPowerAndAirPowerChart(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const secondKey = 'airPower';
    const min = that.yScales[chartName].domain()[0];
    // const max = that.yScales[chartName].domain()[1]
    const className = that.attrGroupClassName(attr);
    let chart = that.content.select(`.${className}`);
    let chartData = attr.data || that.chartData;

    if (attr.filter && attr.filter instanceof Function) {
      chartData = chartData.filter((d, i) => {
        return attr.filter(d, i, attr);
      });
    }

    const areaFun = d3
      .area()
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y0((d, i) => {
        const num = [undefined].includes(getDataValue(attr, d, i)) ? min : getDataValue(attr, d, i);
        return that.contentHeight - that.yScales[chartName](num);
      })
      .y1((d, i) => {
        const num = [undefined].includes(getDataValue(attr, d, i)) ? min : getDataValue(attr, d, i);
        const secondNum = [undefined].includes(d[secondKey]) ? 0 : (d[secondKey] / 100) * num;
        return that.contentHeight - that.yScales[chartName](num - secondNum);
      });

    // 定义折线画法
    const polylineFun = d3
      .line()
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y((d, i) => {
        const num = [undefined].includes(getDataValue(attr, d, i)) ? min : getDataValue(attr, d, i);
        return that.contentHeight - that.yScales[chartName](num);
      });

    // 面积图渐变色
    const areaLinerGradientId = that.createLinearGradient(
      `${className}-area`,
      that.colorStrToGradientArray([
        {
          offset: '0%',
          color: '#afb3b3'
        },
        {
          offset: '100%',
          color: '#afb3b3'
        }
      ])
    );
    const linerGradientId = that.createLinearGradient(`${className}-line`, attr.color);

    if (that.isEmptySelect(chart)) {
      // 没有创建过该属性的图表
      chart.remove();
      chart = that.content.append('g').attr('class', className);

      // 从下到上出现动画
      chart
        .append('path')
        .attr('class', 'area')
        .attr('d', areaFun(chartData))
        // .attr('d', areaStrtFun(that.chartData))
        .attr('fill', `url(#${areaLinerGradientId})`);

      that.polylineChartAnimation(
        chart
          .append('path')
          .attr('class', 'line')
          .attr('d', polylineFun(chartData))
          .attr('stroke', `url(#${linerGradientId})`)
          .attr('stroke-width', 1)
          .attr('fill', 'none')
      );
    } else {
      // 已经创建过该属性的图表
      chart
        .select('.area')
        .transition()
        .duration(that.CHART_DURATION)
        .attr('d', areaFun(chartData))
        .attr('fill', `url(#${areaLinerGradientId})`);

      chart
        .select('.line')
        .transition()
        .duration(that.CHART_DURATION)
        .attr('d', polylineFun(chartData))
        .attr('stroke', `url(#${linerGradientId})`)
        .attr('stroke-width', 2)
        .attr('fill', 'none');
    }
  }

  // 画体能测试面积图
  drawTestPaceZoneChart(_attr) {
    const attr = _.cloneDeep(_attr);
    const that = this;
    attr.valueKey = 'testMaxPace';
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const secondKey = 'testMinPace';
    const min = that.yScales[chartName].domain()[0];
    const max = that.yScales[chartName].domain()[1];

    const className = that.attrGroupClassName(attr);
    let chart = that.content.select(`.${className}`);
    let chartData = attr.data || that.chartData;

    if (attr.filter && attr.filter instanceof Function) {
      chartData = chartData.filter((d, i) => {
        return attr.filter(d, i, attr);
      });
    }

    const areaFun = d3
      .area()
      .defined((d, i) => {
        // 控制是否绘制该点（return false不绘制）
        const xValue = d[that.xAxisKey];
        const dataValue = getDataValue(attr, d, i);
        return !(
          [undefined, null, '', 0].includes(xValue) ||
          isNaN(Number(xValue)) ||
          [undefined, null, ''].includes(dataValue) ||
          isNaN(Number(dataValue)) ||
          isNaN(that.yScales[chartName](Number(dataValue)))
        );
      })
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y0((d, i) => {
        let num = [undefined].includes(getDataValue(attr, d, i)) ? min : getDataValue(attr, d, i);
        if (num < max) num = max;
        if (num > min) num = min;
        // console.log('y0', num)
        return that.contentHeight - that.yScales[chartName](num);
      })
      .y1((d, i) => {
        let secondNum = [undefined].includes(d[secondKey]) ? 0 : d[secondKey];
        if (secondNum < max) secondNum = max;
        if (secondNum > min) secondNum = min;
        return that.contentHeight - that.yScales[chartName](secondNum);
      });

    // 定义折线画法

    // 没有创建过该属性的图表
    // chart.remove()
    chart = that.content
      .append('g')
      .attr('class', className + '-area')
      .attr('style', 'z-index:-1');

    // 从下到上出现动画
    chart.append('path').attr('class', 'area').attr('d', areaFun(chartData)).attr('fill', `rgba(1,255,176,0.4)`);
  }

  // 画风阻图表
  drawAirPowerChart(attr) {}
}
